import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildRankChipDataAttrs } from '../../../../../../components/tools/chipColorDataAttr';
import useRankChipCspStyles from '../../../../../../hooks/useRankChipCspStyles';
import { rankChipBgFromRankColors } from '../../../../../../utils/rankChipCspStyles';

function formatPromotionDate(value) {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function promotionSourceLabel(source) {
  if (source === 'override') return 'override';
  return null;
}

function formatRemainingLine(count, singular, pluralLabel) {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 0) return null;
  const label = n === 1 ? singular : pluralLabel;
  return `${n} ${label}`;
}

function formatToprDaysLine(missingDays, requiredDays) {
  const missing = Number(missingDays);
  const required = Number(requiredDays);
  if (!Number.isFinite(missing) || missing < 0) return null;
  if (!Number.isFinite(required) || required < 0) return null;
  const unit = required === 1 ? 'day' : 'days';
  return `${missing} / ${required} ${unit} on current rank remaining`;
}

const TOKEN_REQUIREMENT_KEYS = [
  {
    variant: 'mission',
    key: 'missionToken',
    singular: 'mission token remaining',
    plural: 'mission tokens remaining',
    ariaPrefix: 'Mission tokens',
    shortLabel: 'mission token',
  },
  {
    variant: 'total',
    key: 'globalToken',
    singular: 'service XP remaining',  
    plural: 'service XP remaining',
    ariaPrefix: 'Service XP',
    shortLabel: 'service XP',
  },
];

/** Same source as the “i” tooltip: required threshold vs missing count. */
function resolveTokenRequirementProgress(progression, tokenKey) {
  if (!progression?.required || !progression?.missing) return null;

  const required = Number(progression.required[tokenKey]);
  const missingRaw = Number(progression.missing[tokenKey]);
  if (!Number.isFinite(required) || required <= 0) return null;

  const missing = Math.max(
    0,
    Number.isFinite(missingRaw) ? missingRaw : required,
  );
  const achieved = Math.max(0, required - missing);

  return { required, missing, achieved };
}

/** TOPR days on current rank (required.toprDays / missing.toprDays). */
function resolveToprRequirementProgress(progression) {
  return resolveTokenRequirementProgress(progression, 'toprDays');
}

function RankTokenProgressBar({
  variant,
  achieved,
  required,
  missing,
  singular,
  plural,
  ariaPrefix,
  shortLabel,
  title,
}) {
  const value = Math.min(achieved, required);
  const isComplete = value >= required;
  const fillPct = required > 0 ? Math.min(100, Math.round((value / required) * 100)) : 0;
  const remainingLine = formatRemainingLine(missing, singular, plural);
  const tooltipTitle =
    title ??
    (remainingLine ? `${value} / ${required} · ${remainingLine}` : `${value} / ${required}`);

  return (
    <div
      className={
        'member-dashboard__rank-token-bar member-dashboard__rank-token-bar--' +
        variant +
        (isComplete ? ' member-dashboard__rank-token-bar--complete' : '')
      }
      title={tooltipTitle}
    >
      <div
        className="member-dashboard__rank-token-bar__track"
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={required}
        aria-label={`${ariaPrefix}: ${value} of ${required}`}
      >
        <span
          className="member-dashboard__rank-token-bar__fill"
          data-fill-pct={fillPct}
          aria-hidden
        />
        <span className="member-dashboard__rank-token-bar__label">
          <span className="member-dashboard__rank-token-bar__counts">
            {value} / {required}
          </span>
          <span className="member-dashboard__rank-token-bar__type">{shortLabel}</span>
        </span>
      </div>
    </div>
  );
}

function RankUpcomingCourseRequirements({ requiredCourses }) {
  const courses = Array.isArray(requiredCourses) ? requiredCourses : [];
  if (!courses.length) return null;

  return (
    <div className="member-dashboard__rank-upcoming-courses">
      <p className="member-dashboard__rank-upcoming-courses__title">Required courses</p>
      <ul className="member-dashboard__rank-upcoming-courses-list">
        {courses.map((course) => {
          const label = course.displayName || `Course ${course.courseId}`;
          const versionSuffix = course.version ? ` (v${course.version})` : '';
          return (
            <li
              key={course.courseId}
              className={
                course.completed
                  ? 'member-dashboard__rank-upcoming-courses-item--met'
                  : ''
              }
            >
              {label}
              {versionSuffix}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RankUpcomingTokenBars({ progression, tokensRedacted }) {
  if (!progression?.required || !progression?.missing) return null;

  const bars = [];

  if (!tokensRedacted) {
    for (const { variant, key, singular, plural, ariaPrefix, shortLabel } of TOKEN_REQUIREMENT_KEYS) {
      const metrics = resolveTokenRequirementProgress(progression, key);
      if (!metrics) continue;

      bars.push(
        <RankTokenProgressBar
          key={variant}
          variant={variant}
          achieved={metrics.achieved}
          required={metrics.required}
          missing={metrics.missing}
          singular={singular}
          plural={plural}
          ariaPrefix={ariaPrefix}
          shortLabel={shortLabel}
        />,
      );
    }
  }

  const toprMetrics = resolveToprRequirementProgress(progression);
  if (toprMetrics) {
    bars.push(
      <RankTokenProgressBar
        key="time"
        variant="time"
        achieved={toprMetrics.achieved}
        required={toprMetrics.required}
        missing={toprMetrics.missing}
        ariaPrefix="Time on current rank"
        shortLabel="days on rank"
        title={formatToprDaysLine(toprMetrics.missing, toprMetrics.required)}
      />,
    );
  }

  if (!bars.length) return null;

  return (
    <div className="member-dashboard__rank-upcoming-token-bars">{bars}</div>
  );
}


function buildProgressionRequirementItems(progression, tokensRedacted) {
  if (!progression?.missing) return [];

  const items = [];

  if (!tokensRedacted) {
    for (const { key, singular, plural } of TOKEN_REQUIREMENT_KEYS) {
      const metrics = resolveTokenRequirementProgress(progression, key);
      if (!metrics) continue;
      items.push({
        key: key === 'globalToken' ? 'total' : 'mission',
        remaining: metrics.missing,
        line: formatRemainingLine(metrics.missing, singular, plural),
      });
    }
  }

  const toprMetrics = resolveToprRequirementProgress(progression);
  if (toprMetrics) {
    items.push({
      key: 'topr',
      remaining: toprMetrics.missing,
      line: formatToprDaysLine(toprMetrics.missing, toprMetrics.required),
    });
  }

  return items.filter((item) => item.line);
}

function RankProgressionRequirementsTooltip({ progression, tokensRedacted }) {
  const items = buildProgressionRequirementItems(progression, tokensRedacted);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const root = rootRef.current;
      if (!root || root.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [open]);

  if (!items.length) return null;

  return (
    <div className="member-dashboard__rank-upcoming-info" ref={rootRef}>
      <button
        type="button"
        className="member-dashboard__rank-upcoming-info__btn"
        aria-label="Promotion requirements"
        aria-expanded={open ? 'true' : 'false'}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        i
      </button>
      {open ? (
        <div
          className="member-dashboard__rank-upcoming-info__tooltip"
          role="dialog"
          aria-label="Promotion requirements remaining"
        >
          <ul className="member-dashboard__rank-timeline-requirements">
            {items.map(({ key, line, remaining }) => (
              <li
                key={key}
                className={
                  Number(remaining) === 0
                    ? 'member-dashboard__rank-timeline-requirements-item--met'
                    : ''
                }
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function TimelineItem({ row, isLast, showConnector, isCurrent, endAt }) {
  const chipAttrs = buildRankChipDataAttrs(row.rankColors);
  const hasRankTint = Boolean(chipAttrs['data-chip-bg']);
  const sourceLabel = promotionSourceLabel(row.promotionSource);
  const sourceSuffix = sourceLabel ? (
    <span className="member-dashboard__rank-timeline-source">
      {' '}
      · {sourceLabel}
    </span>
  ) : null;

  return (
    <li
      className={
        'member-dashboard__rank-timeline-item' +
        (isLast ? ' member-dashboard__rank-timeline-item--last' : '') +
        (isCurrent ? ' member-dashboard__rank-timeline-item--current' : '')
      }
    >
      <div className="member-dashboard__rank-timeline-rail" aria-hidden>
        <span className="member-dashboard__rank-timeline-dot" />
        {showConnector ? (
          <span className="member-dashboard__rank-timeline-line" />
        ) : null}
      </div>

      <div className="member-dashboard__rank-timeline-content">
        <div className="member-dashboard__rank-timeline-icon-slot">
          {row.rankImageUrl ? (
            <img
              className="member-dashboard__rank-timeline-icon"
              src={row.rankImageUrl}
              alt=""
            />
          ) : (
            <span
              className="member-dashboard__rank-timeline-icon--empty"
              aria-hidden
            />
          )}
        </div>

        <div className="member-dashboard__rank-timeline-text">
          <p
            className={
              'member-dashboard__rank-timeline-rank' +
              (hasRankTint
                ? ' member-dashboard__rank-timeline-rank--chip member-dashboard__rank-timeline-rank--rank-tinted'
                : '')
            }
            {...chipAttrs}
          >
            {row.rankName || '—'}
          </p>
          <p className="member-dashboard__rank-timeline-date">
            {endAt ? (
              <>
                <span className="member-dashboard__rank-timeline-date-since">from </span>
                {formatPromotionDate(row.achievedAt)}
                <span className="member-dashboard__rank-timeline-date-since"> to </span>
                {formatPromotionDate(endAt)}
              </>
            ) : (
              <>
                <span className="member-dashboard__rank-timeline-date-since">since </span>
                {formatPromotionDate(row.achievedAt)}
              </>
            )}
            {sourceSuffix}
          </p>
        </div>
      </div>
    </li>
  );
}

function UpcomingRankItem({ nextRank, progression, tokensRedacted, isLast, showConnector }) {
  return (
    <li
      className={
        'member-dashboard__rank-timeline-item member-dashboard__rank-timeline-item--upcoming' +
        (isLast ? ' member-dashboard__rank-timeline-item--last' : '')
      }
    >
      <div className="member-dashboard__rank-timeline-rail" aria-hidden>
        <span className="member-dashboard__rank-timeline-dot" />
        {showConnector ? (
          <span className="member-dashboard__rank-timeline-line member-dashboard__rank-timeline-line--dashed" />
        ) : null}
      </div>

      <div className="member-dashboard__rank-timeline-content">
        <div className="member-dashboard__rank-upcoming-frame">
          <p className="member-dashboard__rank-upcoming-frame__title">Next rank</p>
          <div className="member-dashboard__rank-upcoming-frame__body">
            <div className="member-dashboard__rank-timeline-icon-slot">
              {nextRank.rankImageUrl ? (
                <img
                  className="member-dashboard__rank-timeline-icon"
                  src={nextRank.rankImageUrl}
                  alt=""
                />
              ) : (
                <span
                  className="member-dashboard__rank-timeline-icon--empty"
                  aria-hidden
                />
              )}
            </div>

            <div className="member-dashboard__rank-timeline-text">
              <div className="member-dashboard__rank-upcoming-head">
                <p className="member-dashboard__rank-timeline-rank member-dashboard__rank-timeline-rank--upcoming">
                  {nextRank.rankName || '—'}
                </p>
                <RankProgressionRequirementsTooltip
                  progression={progression}
                  tokensRedacted={tokensRedacted}
                />
              </div>
            </div>
          </div>
          <RankUpcomingCourseRequirements
            requiredCourses={progression?.requiredCourses}
          />
          <RankUpcomingTokenBars
            progression={progression}
            tokensRedacted={tokensRedacted}
          />
        </div>
      </div>
    </li>
  );
}

export default function RankHistoryTimeline({
  promotions,
  nextRank,
  progression,
  tokensRedacted,
}) {
  const rowsAsc = Array.isArray(promotions) ? promotions : [];
  const showUpcoming = Boolean(nextRank?.rankName || nextRank?.rankId);
  const historyNewestFirst = [...rowsAsc].reverse();
  const totalItems = historyNewestFirst.length + (showUpcoming ? 1 : 0);

  const timelineChipColors = useMemo(
    () => rowsAsc.map((row) => rankChipBgFromRankColors(row.rankColors)).filter(Boolean),
    [rowsAsc],
  );
  useRankChipCspStyles(timelineChipColors);

  if (!rowsAsc.length) {
    return (
      <p className="member-dashboard__panel-placeholder">
        No rank promotions recorded for this handle.
      </p>
    );
  }

  return (
    <div className="member-dashboard__rank-timeline-viewport">
      <ol className="member-dashboard__rank-timeline">
        {showUpcoming ? (
          <UpcomingRankItem
            nextRank={nextRank}
            progression={progression}
            tokensRedacted={tokensRedacted}
            isLast={totalItems === 1}
            showConnector={totalItems > 1}
          />
        ) : null}
        {historyNewestFirst.map((row, index) => {
          const displayIndex = showUpcoming ? index + 1 : index;
          const isLast = displayIndex === totalItems - 1;
          const showConnector = displayIndex < totalItems - 1;
          const newerRow = index > 0 ? historyNewestFirst[index - 1] : null;

          return (
            <TimelineItem
              key={row.id ?? `${row.rankId}-${row.achievedAt}`}
              row={row}
              isLast={isLast}
              showConnector={showConnector}
              isCurrent={index === 0}
              endAt={newerRow?.achievedAt ?? null}
            />
          );
        })}
      </ol>
    </div>
  );
}
